import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const { RangePicker } = DatePicker;

const formatRangeValue = (record) => {
  if (!record?.startDate || !record?.endDate) {
    return undefined;
  }
  return [dayjs(record.startDate), dayjs(record.endDate)];
};

const parseEnumData = (raw) => {
  if (!raw) return [];
  const source = typeof raw === 'string' ? (() => {
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('解析活动分类失败', err);
      return [];
    }
  })() : raw;
  return (Array.isArray(source) ? source : [])
    .filter((item) => item?.enumCode && item?.enumName)
    .sort((a, b) => (a.showSort || 0) - (b.showSort || 0))
    .map((item) => ({
      key: item.enumCode,
      label: item.enumName,
      raw: item,
    }));
};

const EventPublish = () => {
  const { apiClient } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState();
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(null);

  const [events, setEvents] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadedPicUrl, setUploadedPicUrl] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    if (!apiClient || categories.length > 0) return; // 只在首次加载且没有分类数据时请求
    const fetchCategories = async () => {
      setCategoryLoading(true);
      setCategoryError(null);
      try {
        const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
          tableName: 'EVENT_TYPE',
          colName: 'EVENT_TYPE',
        });
        const parsed = parseEnumData(resp?.data?.data);
        setCategories(parsed);
        if (!activeCategory && parsed.length > 0) {
          setActiveCategory(parsed[0].key);
        }
      } catch (err) {
        console.error('获取活动分类失败', err);
        setCategoryError('获取活动分类失败，请稍后再试');
        message.error('获取活动分类失败');
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, [apiClient, categories.length]); // 依赖项改为 categories.length

  const fetchEvents = useCallback(
    async (groupNo, nextPageNo, nextPageSize) => {
      if (!apiClient || !groupNo) return;
      setListLoading(true);
      setListError(null);
      try {
        // 获取用户ID
        const userStr = localStorage.getItem('user') || '{}';
        const user = JSON.parse(userStr);
        const userId = `${user.customerId || 'S00001'}-${user.userId || ''}`;
        
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/event/pageList', {
          groupNo,
          pageSize: nextPageSize,
          pageNo: nextPageNo,
          userId: userId
        });
        const data = resp?.data?.data || {};
        const list = data.list || data.records || [];
        const totalRecord = data.totalRecord || data.total || list.length;
        setEvents(list);
        setTotal(totalRecord);
      } catch (err) {
        console.error('获取活动列表失败', err);
        setListError('获取活动列表失败，请稍后重试');
        message.error('获取活动列表失败');
      } finally {
        setListLoading(false);
      }
    },
    [apiClient]
  );

  useEffect(() => {
    if (!activeCategory) return;
    fetchEvents(activeCategory, pageNo, pageSize);
  }, [activeCategory, pageNo, pageSize, fetchEvents]);

  const tabItems = useMemo(
    () =>
      categories.map((cat) => ({
        key: cat.key,
        label: cat.label,
      })),
    [categories]
  );

  const handleTabChange = (key) => {
    setActiveCategory(key);
    setPageNo(1);
  };

  const handleTableChange = (pagination) => {
    if (pagination.current !== pageNo) {
      setPageNo(pagination.current);
    }
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPageNo(1);
    }
  };

  const resetFormState = () => {
    form.resetFields();
    form.setFieldsValue({ state: '1' });
    setUploadedPicUrl('');
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    resetFormState();
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    resetFormState();
    form.setFieldsValue({
      eventName: record.eventName,
      cycle: record.cycle,
      enableCycleCreate: record.enableCycleCreate ?? false,
      points: record.points,
      dateRange: formatRangeValue(record),
      state: record.state ?? '0',
      remark: record.remark,
      eventLink: record.eventLink,
      target: record.target,
      completeMode: record.completeMode ?? '1',
      // 设置只读字段
      createUser: record.createUser,
      updateUser: record.updateUser,
    });
    setUploadedPicUrl(record.picUrl || '');
    setModalVisible(true);
  };

  const openCopyModal = (record) => {
    setEditingRecord(null);
    resetFormState();
    form.setFieldsValue({
      eventName: record.eventName + ' - 复制',
      cycle: record.cycle,
      enableCycleCreate: record.enableCycleCreate ?? false,
      points: record.points,
      dateRange: formatRangeValue(record),
      state: record.state ?? '0',
      remark: record.remark,
      eventLink: record.eventLink,
      target: record.target,
      completeMode: record.completeMode ?? '1',
    });
    setUploadedPicUrl(record.picUrl || '');
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setUploadedPicUrl('');
  };

  const submitForm = async () => {
    if (!activeCategory) {
      message.warning('请先选择活动分类');
      return;
    }
    try {
      const values = await form.validateFields();
      
      // 获取用户信息
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr);
      const userName = user.name || '';
      
      const payload = {
        ...values,
        groupNo: activeCategory,
        id: editingRecord?.id,
        startDate: values.dateRange?.[0]?.format('YYYY-MM-DD') + ' 00:00:01',
        endDate: values.dateRange?.[1]?.format('YYYY-MM-DD') + ' 23:59:59',
        picUrl: uploadedPicUrl,
      };
      
      // 根据是新增还是编辑添加不同的用户参数
      if (editingRecord) {
        // 编辑时添加更新人
        payload.updateUser = userName;
      } else {
        // 新增时添加创建人
        payload.createUser = userName;
      }
      
      delete payload.dateRange;
      setModalSubmitting(true);
      await apiClient.post('/sports-server-prod/taller.api.v1/event/add', payload);
      message.success(editingRecord ? '活动已更新' : '活动已创建');
      closeModal();
      fetchEvents(activeCategory, pageNo, pageSize);
    } catch (err) {
      if (err?.errorFields) return;
      console.error('保存活动失败', err);
      message.error('保存活动失败，请稍后重试');
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record?.id) {
      message.warning('当前记录缺少标识，无法删除');
      return;
    }
    try {
      setDeletingId(record.id);
      await apiClient.post('/sports-server-prod/taller.api.v1/event/del', {
        id: record.id,
        groupNo: record.groupNo || activeCategory,
      });
      message.success('活动已删除');
      fetchEvents(activeCategory, pageNo, pageSize);
    } catch (err) {
      console.error('删除活动失败', err);
      message.error('删除活动失败，请稍后再试');
    } finally {
      setDeletingId(null);
    }
  };

  // Upload props
  const uploadProps = {
    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await apiClient.post('/sports-server-prod/sys.api.v1/file/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess(response.data);
      } catch (error) {
        onError(error);
      }
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        message.success('上传成功');
        const data = info.file.response.data;
        const key = Object.keys(data)[0];
        const value = data[key];
        setUploadedPicUrl(value);
      } else if (info.file.status === 'error') {
        message.error('上传失败');
      }
    },
    maxCount: 1,
    showUploadList: false,
  };

  const columns = [
    {
      title: '活动图片',
      key: 'picUrl',
      render: (_, record) => (
        record.picUrl ? (
          <Image
            src={record.picUrl}
            alt="活动图片"
            width={60}
            height={60}
            style={{ borderRadius: 4, objectFit: 'cover' }}
            preview={{
              src: record.picUrl,
            }}
          />
        ) : '-'
      ),
      width: 100,
      fixed: 'left',
    },
    {
      title: '活动名称',
      dataIndex: 'eventName',
      key: 'eventName',
      width: 200,
      render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{text}</div>,
      fixed: 'left',
    },

    {
      title: '活动时间',
      key: 'dateRange',
      render: (_, record) => {
        if (!record.startDate || !record.endDate) return '-';
        return `${dayjs(record.startDate).format('YYYY-MM-DD')} ~ ${dayjs(record.endDate).format('YYYY-MM-DD')}`;
      },
      width: 260,
    },
    {
      title: '奖励积分',
      dataIndex: 'points',
      key: 'points',
      width: 110,
    },
    {
      title: '活动备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      width: 150,
    },
    {
      title: '活动链接',
      dataIndex: 'eventLink',
      key: 'eventLink',
      width: 160,
      render: (value) => (value ? (
        <a href={value} target="_blank" rel="noreferrer">查看</a>
      ) : '-'),
    },

    {
      title: '开启状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (value) => (
        <Tag color={value === '1' ? 'green' : 'default'}>{value === '1' ? '已开启' : '未开启'}</Tag>
      ),
    },
    {
      title: '活动状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (value) => {
        let color = 'default';
        if (value === '进行中') color = 'green';
        else if (value === '已结束') color = 'red';
        else if (value === '未开始') color = 'orange';
        return <Tag color={color}>{value || '-'}</Tag>;
      },
    },

    {
      title: '发布人',
      dataIndex: 'createUser',
      key: 'createUser',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: '最后修改人',
      dataIndex: 'updateUser',
      key: 'updateUser',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: '完成模式',
      dataIndex: 'completeMode',
      key: 'completeMode',
      width: 120,
      render: (value) => {
        const modeMap = {
          '1': '自动完成',
          '2': '拍照完成',
          '3': '拍照审批完成'
        };
        return modeMap[value] || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" onClick={() => openEditModal(record)}>编辑</Button>
          <Button type="link" icon={<CopyOutlined />} onClick={() => openCopyModal(record)}>复制</Button>
          <Popconfirm
            title="确定删除该活动吗？"
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger loading={deletingId === record.id}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} disabled={!activeCategory}>
              发布活动
            </Button>
            <div style={{ flex: 1, marginLeft: 16 }}>
              {categoryLoading ? (
                <Space>
                  <Spin size="small" />
                  <span>正在加载分类...</span>
                </Space>
              ) : (
                <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: 8 }}>
                  <Tabs
                    activeKey={activeCategory}
                    items={tabItems}
                    onChange={handleTabChange}
                    destroyInactiveTabPane
                    tabBarGutter={20}
                    style={{ display: 'inline-block' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {categoryError && (
          <div style={{ marginBottom: 16, color: '#ff4d4f' }}>{categoryError}</div>
        )}
        {listError && (
          <div style={{ marginBottom: 16, color: '#ff4d4f' }}>{listError}</div>
        )}
        <Table
          rowKey={(record) => record.id || `${record.eventName}-${record.startDate}`}
          loading={listLoading}
          columns={columns}
          dataSource={events}
          scroll={{ x: 1200, y: 400 }}
          pagination={{
            current: pageNo,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑活动' : '发布活动'}
        open={modalVisible}
        onCancel={closeModal}
        onOk={submitForm}
        confirmLoading={modalSubmitting}
        destroyOnClose
        width={640}
      >
        <Form layout="vertical" form={form} initialValues={{ state: '1' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <Form.Item label="活动名称" name="eventName" rules={[{ required: true, message: '请输入活动名称', max: 30 }]}>
              <Input placeholder="请输入活动名称" maxLength={30} />
            </Form.Item>
            <Form.Item label="活动链接" name="eventLink" rules={[{ max: 80, message: '活动链接不能超过80个字符' }]}>
              <Input placeholder="请输入活动链接" maxLength={80} />
            </Form.Item>
            {!editingRecord && (
              <Form.Item label="轮询频次" name="cycle"> 
                <Select placeholder="请选择轮询频次" allowClear>
                  <Select.Option value="每日">每日</Select.Option>
                  <Select.Option value="每周">每周</Select.Option>
                  <Select.Option value="每月">每月</Select.Option>
                  <Select.Option value="每季度">每季度</Select.Option>
                  <Select.Option value="每半年">每半年</Select.Option>
                  <Select.Option value="每年">每年</Select.Option>
                </Select>
              </Form.Item>
            )}
            {!editingRecord && (
              <Form.Item name="enableCycleCreate" valuePropName="checked">
                <Checkbox>是否启用循环创建模式</Checkbox>
              </Form.Item>
            )}
            <Form.Item label="活动时间" name="dateRange" rules={[{ required: true, message: '请选择活动时间范围' }]}>
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="奖励积分" name="points">
              <InputNumber style={{ width: '100%' }} min={0} precision={0} placeholder="请输入奖励积分" />
            </Form.Item>
            <Form.Item label="是否开启" name="state">
              <Select options={[{ label: '未开启', value: '0' }, { label: '已开启', value: '1' }]} />
            </Form.Item>
            <Form.Item label="完成模式" name="completeMode" rules={[{ required: true, message: '请选择完成模式' }]}> 
              <Select placeholder="请选择完成模式">
                <Select.Option value="1">自动完成</Select.Option>
                <Select.Option value="2">拍照完成</Select.Option>
                <Select.Option value="3">拍照审批完成</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="活动备注" name="remark" rules={[{ max: 80, message: '活动备注不能超过80个字符' }]}> 
              <Input.TextArea rows={3} placeholder="请输入活动备注" maxLength={80} />
            </Form.Item>
            <Form.Item label="封面图片">
              <Upload {...uploadProps} accept="image/*">
                <Button icon={<UploadOutlined />}>{uploadedPicUrl ? '重新上传' : '上传图片'}</Button>
              </Upload>
              {uploadedPicUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={uploadedPicUrl}
                    alt="封面图片"
                    style={{ maxWidth: 200, maxHeight: 200, borderRadius: 4 }}
                  />
                </div>
              )}
            </Form.Item>
          </div>
          {/* 隐藏字段用于存储用户信息 */}
          <Form.Item name="createUser" noStyle>
            <input type="hidden" />
          </Form.Item>
          <Form.Item name="updateUser" noStyle>
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EventPublish;

